import AlignLeftOutlined from '@ant-design/icons/lib/icons/AlignLeftOutlined';
import CopyOutlined from '@ant-design/icons/lib/icons/CopyOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Col from 'antd/lib/col';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Switch from 'antd/lib/switch';
import Tag from 'antd/lib/tag';
import Typography from 'antd/lib/typography';
import Upload from 'antd/lib/upload';
import trim from 'lodash/trim';
import React, { useState } from 'react';
import { CloudVenLogsModal } from 'src/components/modals/CloudVenLogsModal/CloudVenLogsModal';
import { CloudVenDispatchTypeMethodSelect } from '../../../components/selectors/CloudVenDispatchByMethodSelect/CloudVenDispatchTypeMethodSelect';
import { CompanySelector } from '../../../components/selectors/CompanySelector/CompanySelector';
import { ProgramSelector } from '../../../components/selectors/ProgramSelector/ProgramSelector';
import { SiteSelector } from '../../../components/selectors/SiteSelector/SiteSelector';
import { useAuth } from '../../../domain/auth/useAuth';
import { COMPANY_TYPE } from '../../../domain/company/interface';
import { saveCloudVen } from '../../../domain/ven';
import { copyVenToClipboard } from '../../../domain/ven/copyVenToClipboard';
import { ICloudVen, profileOptions } from '../../../domain/ven/interface';
import { CertificateFingerprints } from './CertificateFingerprints';
import { ICloudVenFormValues, mapCloudVenToFormValues, mapFormValuesToCloudVenDto, TCloudVENDispatchType } from './CloudVenForm';

interface IVenFormProps {
    ven: ICloudVen | Partial<ICloudVen>;
    onClose: Function;
}

export function CloudVenFormModal({ ven, onClose }: IVenFormProps) {
    const { notification } = App.useApp();
    const auth = useAuth();
    const [form] = Form.useForm<ICloudVenFormValues>();
    const [loading, setLoading] = useState<boolean>(false);
    const [showLogs, setShowLogs] = useState<boolean>(false);
    const [cert, setCert] = useState<string | undefined>(ven.ven_cert);
    const [key, setKey] = useState<string | undefined>(ven.ven_key);
    const dispatchType: TCloudVENDispatchType | null = Form.useWatch('dispatch_type', form) || null;
    const companyId: number | undefined = Form.useWatch('company_id', form);
    const programIds: number[] | undefined = Form.useWatch('program_ids', form);
    const isEditing = Boolean(ven.id);

    async function onFormFinish(formVen: ICloudVenFormValues) {
        try {
            setLoading(true);

            const venDto = mapFormValuesToCloudVenDto(formVen, {
                cert,
                key,
            });

            await saveCloudVen(venDto);
            notification.info({ key: 'ven-save-info', message: 'VEN saved' });
            onClose(ven);
        } catch (err: any) {
            notification.error({ key: 'ven-save-error', message: err.message || 'Cannot save VEN!' });
        } finally {
            setLoading(false);
        }
    }

    function closeModal() {
        onClose();
    }

    function pemToVirtualFile(pem: string | null | undefined, filename: string) {
        if (pem) {
            return [{ uid: '1', name: filename }];
        }
        return [];
    }

    const sslCertProps = {
        multiple: false,
        accept: '.pem',
        maxCount: 1,
        onRemove: (file: any) => {
            setCert('');
        },
        beforeUpload: (file: any) => {
            return false;
        },
        onChange: (info: any) => {
            if (info.file.status || !info.fileList.length) {
                return;
            }

            let reader = new FileReader();
            reader.readAsText(info.file, 'UTF-8');
            reader.onload = function () {
                setCert('');
                setCert(reader.result as any);
            };
        },
        defaultFileList: pemToVirtualFile(ven.ven_cert, 'cert.pem'),
    };

    const sslKeyProps = {
        multiple: false,
        accept: '.pem',
        maxCount: 1,
        onRemove: (file: any) => {
            setKey('');
        },
        beforeUpload: (file: any) => {
            return false;
        },
        onChange: (info: any) => {
            if (info.file.status || !info.fileList.length) {
                return;
            }

            let reader = new FileReader();
            reader.readAsText(info.file, 'UTF-8');
            reader.onload = function () {
                setKey(reader.result as any);
            };
        },
        defaultFileList: pemToVirtualFile(ven.ven_key, 'key.pem'),
    };

    const onCopy = async () => {
        await copyVenToClipboard(form.getFieldsValue());
    };

    function showLogsModal() {
        setShowLogs(true);
    }

    return (
        <Modal
            open
            destroyOnHidden
            title={isEditing ? 'Edit Cloud VEN' : 'New Cloud VEN'}
            onCancel={closeModal}
            footer={[
                isEditing && (
                    <div key="ven-modal-show-logs" style={{ width: '100%', display: 'flex' }}>
                        <Button icon={<AlignLeftOutlined />} onClick={showLogsModal}>
                            Show Logs
                        </Button>
                    </div>
                ),
                <Button key="ven-modal-cancel" onClick={closeModal}>
                    Cancel
                </Button>,
                <Button key="ven-modal-submit" type="primary" loading={loading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form form={form} name="ven-form" preserve={false} layout="vertical" onFinish={onFormFinish} initialValues={mapCloudVenToFormValues(ven)}>
                <Form.Item hidden name="id">
                    <Input />
                </Form.Item>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item
                            name="ven_username"
                            label={<Typography.Text strong>Username</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter username!' },
                                { max: 30, message: 'Number of characters should be less than 30' },
                            ]}
                            normalize={trim}
                        >
                            <Input placeholder="Username" size="large" autoComplete="cloud-ven-username" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ven_password"
                            label={<Typography.Text strong>Password</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter password!' },
                                { max: 30, message: 'Number of characters should be less than 30' },
                            ]}
                            normalize={trim}
                        >
                            <Input.Password placeholder="Password" size="large" autoComplete="new-password" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item
                            name="ven_id"
                            label={<Typography.Text strong>VEN ID</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter VEN ID!' },
                                { max: 30, message: 'Number of characters should be less than 30' },
                            ]}
                            normalize={trim}
                        >
                            <Input placeholder="VEN ID" size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="ven_vtn_id"
                            label={<Typography.Text strong>VTN ID</Typography.Text>}
                            hasFeedback
                            rules={[
                                { required: true, message: 'Please enter VTN ID!' },
                                { max: 30, message: 'Number of characters should be less than 30' },
                            ]}
                            normalize={trim}
                        >
                            <Input placeholder="VTN ID" size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    name="ven_uri"
                    label={<Typography.Text strong>URI</Typography.Text>}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please enter VEN URI!' },
                        { max: 200, message: 'Number of characters should be less than 200' },
                    ]}
                    normalize={trim}
                >
                    <Input placeholder="URI" size="large" />
                </Form.Item>
                <Row justify="end">
                    <Button key="copy-ven-info" type="primary" onClick={onCopy} icon={<CopyOutlined />}>
                        Copy VEN Properties
                    </Button>
                </Row>
                <Divider plain>OpenADR Options</Divider>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item labelCol={{ span: 24 }} label={<Typography.Text strong>SSL Cert (PEM)</Typography.Text>}>
                            <Upload {...sslCertProps}>
                                <Button icon={<UploadOutlined />} size="large">
                                    Upload
                                </Button>
                            </Upload>
                            {cert && <CertificateFingerprints certificate={cert} />}
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item labelCol={{ span: 24 }} label={<Typography.Text strong>SSL Key (PEM)</Typography.Text>}>
                            <Upload {...sslKeyProps}>
                                <Button icon={<UploadOutlined />} size="large">
                                    Upload
                                </Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item name="ven_profile" label={<Typography.Text strong>OpenADR Profile</Typography.Text>}>
                            <Select size="large" placeholder="Please select OpenADR type" options={profileOptions}></Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="ven_registration_id" label={<Typography.Text strong>Registration ID</Typography.Text>}>
                            <Input size="large" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider plain>Dispatch Options</Divider>

                <div>
                    <Form.Item
                        name="dispatch_type"
                        tooltip="How this VEN will be targeted: by Program, by company Site, by Resource ID, or by Group ID"
                        label={<Typography.Text strong>Dispatch By</Typography.Text>}
                        rules={[
                            {
                                message: 'Please select Dispatch By method',
                            },
                        ]}
                    >
                        <CloudVenDispatchTypeMethodSelect />
                    </Form.Item>

                    {dispatchType === 'program' && (
                        <Form.Item
                            name="program_ids"
                            label={<Typography.Text strong>Dispatched Program(s)</Typography.Text>}
                            rules={[
                                {
                                    validator: (_, value: number[] | undefined | null) => {
                                        if (!value || value?.length === 0) return Promise.reject(new Error('Please select at least one program'));
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <ProgramSelector
                                mode="multiple"
                                selectedProgram={programIds as number[]}
                                handleProgram={programs => {}}
                                tagRender={(props: any) => (
                                    <Tag color="gold" closable onClose={props.onClose}>
                                        {props.label}
                                    </Tag>
                                )}
                            />
                        </Form.Item>
                    )}

                    {dispatchType === 'site' && (
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item name="company_id" label={<Typography.Text strong>Company</Typography.Text>}>
                                    <CompanySelector
                                        onlyTypes={[COMPANY_TYPE.CUSTOMER_COMPANY]}
                                        onChange={(value, option) => {
                                            form.setFieldValue('site_id', null);
                                            form.setFieldValue('company_id', value);
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item
                                    name="site_id"
                                    required
                                    label={<Typography.Text strong>Site</Typography.Text>}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.reject(new Error('Please select site'));
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <SiteSelector allowClear={false} companyId={companyId} placeholder="Select Site" />
                                </Form.Item>

                                {!companyId && <Typography.Text type="secondary">Select company first to see sites</Typography.Text>}
                            </Col>
                        </Row>
                    )}

                    {dispatchType === 'resource_id' && (
                        <Alert
                            showIcon
                            type="info"
                            message="Events will be dispatched to sites matching Resource IDs provided in the OpenADR events."
                        />
                    )}

                    {dispatchType === 'group_id' && (
                        <>
                            <Form.Item
                                name="program_ids"
                                label={<Typography.Text strong>Dispatched Program(s)</Typography.Text>}
                                rules={[
                                    {
                                        validator: (_, value: number[] | undefined | null) => {
                                            if (!value || value?.length === 0) return Promise.reject(new Error('Please select at least one program'));
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <ProgramSelector
                                    mode="multiple"
                                    selectedProgram={programIds as number[]}
                                    handleProgram={programs => {}}
                                    tagRender={(props: any) => (
                                        <Tag color="gold" closable onClose={props.onClose}>
                                            {props.label}
                                        </Tag>
                                    )}
                                />
                            </Form.Item>

                            <Alert
                                showIcon
                                type="info"
                                message="Events will be dispatched to sites enrolled in the selected programs matching Group IDs provided in the OpenADR events."
                            />
                        </>
                    )}
                </div>

                <Divider />

                <Row gutter={8}>
                    {auth?.user?.isAdmin() && (
                        <Col span={12}>
                            <Form.Item
                                name="ven_detailed_logging"
                                tooltip="If enabled, more details about communication will be logged"
                                label={<Typography.Text strong>Detailed Logging</Typography.Text>}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    )}

                    <Col span={12}>
                        <Form.Item
                            name="ven_enabled"
                            tooltip="You can temporary disable VEN, instead of deleting it"
                            label={<Typography.Text strong>Enable VEN</Typography.Text>}
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {showLogs && <CloudVenLogsModal onClose={() => setShowLogs(false)} ven={ven} />}
        </Modal>
    );
}
